import { useCallback, useEffect, useState, type ReactNode } from 'react'
import type { IslandProps } from '../registry'
import { supabase } from '../../lib/supabase'
import { getSetting, setSetting } from '../../lib/settings'
import { currentProduct, SHOW_HUNTS_KEY } from '../../lib/product'
import {
  connectEmail,
  fetchBusinessSources,
  onSourceConnected,
  SOURCES_CHANGED_EVENT,
  type EmailProvider,
} from '../../lib/sources'
import { toast } from '../../lib/toast'
import { ProviderPicker } from '../sources/EmailSourceCard'
import { SOURCES_CSS } from '../sources/sourcesStyles'

/**
 * Guided first-run at the top of Today (frontend-simplification Phase 4,
 * extended by docs/proposals/business-sources.md).
 *
 * Runs per business: each business connects its own email and has its own
 * contacts, so a new business gets a fresh guide even after the personal one
 * was finished or dismissed. Everything is done from here, by the client,
 * without leaving Today:
 *   1. Connect email     → inline Gmail / Google Workspace or Zoho Mail buttons
 *                          (OAuth popup; members see who to ask instead)
 *   2. Connect calendar  → Settings > Calendar (Google or Zoho)
 *   3. Add first contacts → import a CSV, or add one by hand
 *   4. Adjust pipeline stages → Settings > Business and stages (optional)
 *
 * Ticks come from real state: business_sources(), the calendar status views,
 * any user_contacts row, and custom stages (or having opened that section).
 * State is re-read when the OAuth popup reports back, when the window regains
 * focus, and when the import / contact drawers close. The card hides once the
 * three required steps are done, or when dismissed (per business). Shown only
 * in Hunts; at /meet/ it waits until the Hunts screens are switched on.
 *
 * Reuses the `.home-welcome*` classes from crm/index.html.
 */

const SR_ONLY = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const

export const DISMISSED_KEY = 'setup_checklist_dismissed'
export const STAGES_SEEN_KEY = 'setup_stages_seen'

/** Dismissal is per business; the personal workspace keeps the original key. */
export function dismissedKeyFor(workspaceId: string | null, personal: boolean): string {
  return !workspaceId || personal ? DISMISSED_KEY : `${DISMISSED_KEY}:${workspaceId}`
}

interface State {
  loaded: boolean
  gmail: boolean
  calendar: boolean
  workspaceId: string | null
  canManage: boolean
  businessName: string | null
  dismissedKey: string
  contacts: boolean
  stages: boolean
  dismissed: boolean
}

type LegacyFn = (...args: unknown[]) => unknown
function callLegacy(name: string, ...args: unknown[]): void {
  const fn = (window as unknown as Record<string, unknown>)[name]
  if (typeof fn === 'function') (fn as LegacyFn)(...args)
}

function openSettingsSection(section: string): void {
  callLegacy('openSettings')
  setTimeout(() => callLegacy('goToSettingsSection', section), 60)
}

function hasCustomStages(): boolean {
  const ws = (globalThis as { __huntsWsSettings?: { stages?: unknown } }).__huntsWsSettings
  if (Array.isArray(ws?.stages) && ws.stages.length > 0) return true
  try {
    const own = JSON.parse(getSetting('custom_stages', '') || 'null') as unknown
    return Array.isArray(own) && own.length > 0
  } catch {
    return false
  }
}

function huntsOn(): boolean {
  return currentProduct() === 'hunts' || getSetting(SHOW_HUNTS_KEY) === 'true'
}

export default function SetupChecklist({ user }: IslandProps) {
  const userId = user?.id ?? null
  const [state, setState] = useState<State>({
    loaded: false,
    gmail: false,
    calendar: false,
    workspaceId: null,
    canManage: true,
    businessName: null,
    dismissedKey: DISMISSED_KEY,
    contacts: false,
    stages: false,
    dismissed: getSetting(DISMISSED_KEY) === 'true',
  })

  const refresh = useCallback(async () => {
    if (!userId) return
    try {
      const [sources, uc, st, gcal, zcal] = await Promise.all([
        fetchBusinessSources(),
        supabase
          .from('user_contacts')
          .select('contact_id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('user_settings')
          .select('setting_key, setting_value')
          .eq('user_id', userId)
          .like('setting_key', 'setup_%'),
        supabase
          .from('google_calendar_connection_status')
          .select('user_id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('zoho_calendar_connection_status')
          .select('user_id', { count: 'exact', head: true })
          .eq('user_id', userId),
      ])
      const current = sources.find((x) => x.is_current) ?? null
      const dismissedKey = dismissedKeyFor(
        current?.workspace_id ?? null,
        current?.workspace_kind !== 'business',
      )
      const settings = new Map(
        ((st.data ?? []) as Array<{ setting_key: string; setting_value: string }>).map((r) => [
          r.setting_key,
          r.setting_value,
        ]),
      )
      setState({
        loaded: true,
        gmail: sources.some((x) => x.is_current && !!x.provider),
        calendar: (gcal.count ?? 0) > 0 || (zcal.count ?? 0) > 0,
        workspaceId: current?.workspace_id ?? null,
        canManage: !current || current.role === 'owner' || current.role === 'admin',
        businessName: current?.workspace_kind === 'business' ? current.workspace_name : null,
        dismissedKey,
        contacts: (uc.count ?? 0) > 0,
        stages: settings.get(STAGES_SEEN_KEY) === 'true' || hasCustomStages(),
        dismissed: settings.get(dismissedKey) === 'true' || getSetting(dismissedKey) === 'true',
      })
    } catch {
      // If we can't tell, don't nag: hide the card.
      setState((s) => ({ ...s, loaded: true, gmail: true, calendar: true, contacts: true }))
    }
  }, [userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Re-check when the import or contact drawer closes, so the tick lands
  // without a reload.
  useEffect(() => {
    const observers = ['modal-import', 'modal-contact'].flatMap((id) => {
      const modal = document.getElementById(id)
      if (!modal) return []
      let wasOpen = modal.classList.contains('open')
      const observer = new MutationObserver(() => {
        const open = modal.classList.contains('open')
        if (wasOpen && !open) void refresh()
        wasOpen = open
      })
      observer.observe(modal, { attributes: true, attributeFilter: ['class'] })
      return [observer]
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [refresh])

  // Re-check after an OAuth popup (email or calendar) and when the user comes
  // back to the tab from Settings or another window.
  useEffect(() => {
    const again = () => void refresh()
    const offEmail = onSourceConnected(({ ok, provider, message }) => {
      const noun = provider === 'gmail' ? 'Gmail' : 'Zoho Mail'
      if (ok) toast(`${noun} connected. Pulling in emails with your contacts now.`, 'success')
      else toast(`${noun} connect failed: ${message || 'unknown error'}`, 'error')
      again()
    })
    const onMessage = (ev: MessageEvent) => {
      const t = (ev?.data as { type?: unknown } | null)?.type
      if (typeof t === 'string' && /oauth-(success|error)$/.test(t)) again()
    }
    window.addEventListener(SOURCES_CHANGED_EVENT, again)
    window.addEventListener('message', onMessage)
    window.addEventListener('focus', again)
    return () => {
      offEmail()
      window.removeEventListener(SOURCES_CHANGED_EVENT, again)
      window.removeEventListener('message', onMessage)
      window.removeEventListener('focus', again)
    }
  }, [refresh])

  const [connecting, setConnecting] = useState(false)
  const startEmail = async (p: EmailProvider) => {
    if (connecting || !state.workspaceId) return
    setConnecting(true)
    try {
      await connectEmail(p, state.workspaceId)
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not start the connection.', 'error')
    } finally {
      setConnecting(false)
    }
  }

  if (!userId || !huntsOn() || !state.loaded || state.dismissed) return null
  if (state.gmail && state.calendar && state.contacts) return null

  const dismiss = () => {
    setState((s) => ({ ...s, dismissed: true }))
    void setSetting(state.dismissedKey, 'true')
  }

  const business = state.businessName
  const steps: Array<{
    key: string
    label: string
    hint: string
    done: boolean
    actions: Array<{ label: string; run: () => void }>
    inline?: ReactNode
  }> = [
    {
      key: 'gmail',
      label: business ? `Connect ${business}'s email` : 'Connect your email',
      hint: state.canManage
        ? 'Sign in with the mailbox you sell from. Emails with your contacts show up on each contact, and you send from that address.'
        : `Only an owner or admin of ${business ?? 'this business'} can connect its email. Ask them to sign in here.`,
      done: state.gmail,
      actions: [],
      inline:
        state.canManage && state.workspaceId ? (
          <ProviderPicker workspaceId={state.workspaceId} busy={connecting} onStart={startEmail} />
        ) : undefined,
    },
    {
      key: 'calendar',
      label: 'Connect your calendar',
      hint: 'Google or Zoho Calendar. Meetings land on Today and people can book time with you.',
      done: state.calendar,
      actions: [{ label: 'Connect', run: () => openSettingsSection('calendar') }],
    },
    {
      key: 'contacts',
      label: 'Add your first contacts',
      hint: 'Import a spreadsheet (columns are matched for you), or add one person to start.',
      done: state.contacts,
      actions: [
        { label: 'Import CSV', run: () => callLegacy('openImport') },
        { label: 'Add one', run: () => callLegacy('openAddContact') },
      ],
    },
    {
      key: 'stages',
      label: 'Adjust pipeline stages',
      hint: 'Optional. The default stages work; rename or reorder them to match how you sell.',
      done: state.stages,
      actions: [
        {
          label: 'Review',
          run: () => {
            setState((s) => ({ ...s, stages: true }))
            void setSetting(STAGES_SEEN_KEY, 'true')
            openSettingsSection('business')
          },
        },
      ],
    },
  ]
  const required = steps.filter((s) => s.key !== 'stages')
  const doneCount = required.filter((s) => s.done).length
  const pct = Math.round((doneCount / required.length) * 100)

  return (
    <section className="home-welcome" aria-labelledby="setup-checklist-title">
      <style>{SOURCES_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="home-welcome-title" id="setup-checklist-title">
            {state.businessName ? `Set up ${state.businessName}` : 'Set up Hunts'}
          </h2>
          <div className="home-welcome-sub">
            {doneCount} of {required.length} done. A few minutes, and Today fills with your emails,
            meetings and next steps.
          </div>
          <div
            role="progressbar"
            aria-label="Setup progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            style={{
              height: 4,
              borderRadius: 2,
              background: 'var(--bg3)',
              margin: '-12px 0 16px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: 'var(--accent)',
                transition: 'width .3s',
              }}
            />
          </div>
        </div>
        <button
          type="button"
          className="modal-close"
          onClick={dismiss}
          aria-label="Hide setup checklist"
          title="Hide"
        >
          ×
        </button>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {steps.map((s, i) => (
          <li key={s.key} className={`home-welcome-step${s.done ? ' done' : ''}`}>
            <div className="home-welcome-num" aria-hidden="true">
              {s.done ? '✓' : i + 1}
            </div>
            <div className="home-welcome-text">
              <div className="home-welcome-label">
                {s.label}
                {s.done && <span style={SR_ONLY}> (done)</span>}
              </div>
              <div className="home-welcome-hint">{s.hint}</div>
              {!s.done && s.inline && <div style={{ marginTop: 10 }}>{s.inline}</div>}
            </div>
            {s.actions.map((a) => (
              <button key={a.label} type="button" className="home-welcome-cta" onClick={a.run}>
                {a.label}
              </button>
            ))}
          </li>
        ))}
      </ol>
    </section>
  )
}
